/**
 * Studios — every studio with its owner, location, plan, billing
 * channel (App Store / Google Play / web / comp), team size, projects,
 * sign-up platform and last activity. Search, filter, sort, CSV export.
 *
 * Data: `adminListStudios`. Filters read/write the URL (`?channel=…`)
 * so dashboard cards can deep-link here.
 */
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconBrandWhatsapp, IconDownload, IconPhone, IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { RecalculateStorageButton } from '@/components/RecalculateStorageButton';
import {
  ChannelBadge,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  PlatformBadge,
  RefreshButton,
  RelTime,
  StatusBadge,
  StorageBar,
  StudioAvatar,
  TierBadge,
  useAdminData,
} from '@/components/ui';
import type { StudioRow } from '@/lib/adminTypes';
import { callAdmin } from '@/lib/firebase';
import {
  CHANNEL_LABEL,
  PLATFORM_LABEL,
  STATUS_LABEL,
  TIER_LABEL,
  downloadCsv,
  fmtBytes,
  fmtDate,
  fmtPhone,
  fmtRelative,
  label,
  waLink,
} from '@/lib/format';

const CHANNEL_FILTERS = [
  { value: 'paying', label: 'Paying (all stores)' },
  { value: 'app_store', label: 'App Store' },
  { value: 'play_store', label: 'Google Play' },
  { value: 'web', label: 'Web (Razorpay)' },
  { value: 'comp', label: 'Admin / comp + trial' },
  { value: 'free', label: 'Free' },
];

const STORAGE_FILTERS = [
  { value: '80', label: 'Storage 80%+ used' },
  { value: 'over', label: 'Over storage limit' },
  { value: 'empty', label: 'No files yet' },
];

function storagePct(s: StudioRow): number {
  return s.storageLimitBytes ? s.storageBytes / s.storageLimitBytes : 0;
}

const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'active', label: 'Recently active' },
  { value: 'projects', label: 'Most projects' },
  { value: 'members', label: 'Largest team' },
  { value: 'storage', label: 'Most storage' },
  { value: 'name', label: 'Name A–Z' },
];

function matchesChannel(s: StudioRow, f: string | null): boolean {
  if (!f) return true;
  if (f === 'paying') return ['app_store', 'play_store', 'web'].includes(s.channel) && s.environment !== 'SANDBOX';
  if (f === 'comp') return s.channel === 'comp' || s.channel === 'trial';
  return s.channel === f;
}

export function Studios() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data, error, loading, reload } = useAdminData(() => callAdmin.listStudios({}));
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);
  const [sort, setSort] = useState<string>('newest');
  const [storage, setStorage] = useState<string | null>(null);
  const channel = params.get('channel');

  const setChannel = (v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set('channel', v);
    else next.delete('channel');
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.rows ?? []).filter((s) => {
      if (tier && s.effectiveTier !== tier) return false;
      if (status && s.status !== status) return false;
      if (platform && (s.owner?.signupPlatform ?? 'unknown') !== platform) return false;
      if (!matchesChannel(s, channel)) return false;
      if (storage === '80' && storagePct(s) < 0.8) return false;
      if (storage === 'over' && storagePct(s) < 1) return false;
      if (storage === 'empty' && s.storageBytes > 0) return false;
      if (!q) return true;
      return [s.name, s.id, s.city, s.state, s.studioEmail, s.owner?.phone, s.owner?.email, s.owner?.name]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
    const by: Record<string, (a: StudioRow, b: StudioRow) => number> = {
      newest: (a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0),
      active: (a, b) => (b.lastActiveAt ?? 0) - (a.lastActiveAt ?? 0),
      projects: (a, b) => b.projectCount - a.projectCount,
      members: (a, b) => b.memberCount - a.memberCount,
      storage: (a, b) => b.storageBytes - a.storageBytes,
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...list].sort(by[sort] ?? by.newest);
  }, [data, search, tier, status, platform, channel, sort, storage]);

  const exportCsv = () =>
    downloadCsv(
      `interiorio-studios-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Studio', 'Studio ID', 'City', 'State', 'Owner phone', 'Owner email', 'Studio email',
        'Plan', 'Status', 'Billing', 'Renews / ends', 'Members', 'Clients', 'Projects',
        'Storage used', 'Storage limit', 'Storage %', 'Files',
        'Signed up', 'Sign-up platform', 'Last active', 'Last platform',
      ],
      rows.map((s) => [
        s.name, s.id, s.city, s.state, s.owner?.phone ?? null, s.owner?.email ?? null, s.studioEmail,
        label(TIER_LABEL, s.effectiveTier), label(STATUS_LABEL, s.status), CHANNEL_LABEL[s.channel],
        s.expiresAt ? fmtDate(s.expiresAt) : null, s.memberCount, s.clientCount, s.projectCount,
        fmtBytes(s.storageBytes), fmtBytes(s.storageLimitBytes),
        s.storageLimitBytes ? Math.round(storagePct(s) * 100) : null, s.storageFiles,
        fmtDate(s.createdAt), PLATFORM_LABEL[s.owner?.signupPlatform ?? 'unknown'],
        s.lastActiveAt ? fmtDate(s.lastActiveAt) : null, PLATFORM_LABEL[s.owner?.lastPlatform ?? 'unknown'],
      ]),
    );

  if (error && !data) return <ErrorBlock message={error} onRetry={reload} />;

  return (
    <>
      <PageHeader
        title="Studios"
        subtitle={data ? `${rows.length} of ${data.rows.length} studios` : 'Loading…'}
        actions={
          <>
            <RecalculateStorageButton onDone={reload} />
            <Button
              variant="default"
              size="xs"
              leftSection={<IconDownload size={14} />}
              onClick={exportCsv}
              disabled={!rows.length}
            >
              Export CSV
            </Button>
            <RefreshButton onClick={reload} loading={loading} />
          </>
        }
      />

      <Card withBorder radius="lg" p="md" mb="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search studio, city, phone, email or ID…"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: '1 1 260px' }}
          />
          <Select
            placeholder="Any plan"
            data={Object.entries(TIER_LABEL).map(([value, l]) => ({ value, label: l }))}
            value={tier}
            onChange={setTier}
            clearable
            w={130}
          />
          <Select
            placeholder="Any status"
            data={Object.entries(STATUS_LABEL).map(([value, l]) => ({ value, label: l }))}
            value={status}
            onChange={setStatus}
            clearable
            w={140}
          />
          <Select
            placeholder="Any billing"
            data={CHANNEL_FILTERS}
            value={channel}
            onChange={setChannel}
            clearable
            w={190}
          />
          <Select
            placeholder="Any sign-up platform"
            data={(['ios', 'android', 'web', 'mobile', 'unknown'] as const).map((p) => ({ value: p, label: PLATFORM_LABEL[p] }))}
            value={platform}
            onChange={setPlatform}
            clearable
            w={190}
          />
          <Select placeholder="Any storage" data={STORAGE_FILTERS} value={storage} onChange={setStorage} clearable w={180} />
          <Select data={SORTS} value={sort} onChange={(v) => setSort(v ?? 'newest')} w={160} allowDeselect={false} />
        </Group>
      </Card>

      {!data ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <Card withBorder radius="lg">
          <EmptyState title="No studios match these filters" />
        </Card>
      ) : (
        <Card withBorder radius="lg" p={0}>
          <Table.ScrollContainer minWidth={1240}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="#F8FAFC">
                <Table.Tr>
                  <Table.Th>Studio</Table.Th>
                  <Table.Th>Owner</Table.Th>
                  <Table.Th>Plan</Table.Th>
                  <Table.Th>Billing</Table.Th>
                  <Table.Th>Team</Table.Th>
                  <Table.Th>Projects</Table.Th>
                  <Table.Th w={170}>Storage</Table.Th>
                  <Table.Th>Signed up</Table.Th>
                  <Table.Th>Last active</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((s) => (
                  <Table.Tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/studios/${s.id}`)}>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <StudioAvatar name={s.name} logoUrl={s.logoUrl} />
                        <Stack gap={0} style={{ minWidth: 0 }}>
                          <Text size="sm" fw={600} truncate maw={220}>
                            {s.name}
                          </Text>
                          <Text size="xs" c="dimmed" truncate maw={220}>
                            {[s.city, s.state].filter(Boolean).join(', ') || 'No address yet'}
                          </Text>
                        </Stack>
                      </Group>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Stack gap={2}>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">{fmtPhone(s.owner?.phone ?? null)}</Text>
                          {s.owner?.phone ? (
                            <>
                              <Tooltip label="Call">
                                <ActionIcon component="a" href={`tel:${s.owner.phone}`} size="sm" variant="subtle">
                                  <IconPhone size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="WhatsApp">
                                <ActionIcon
                                  component="a"
                                  href={waLink(s.owner.phone) ?? undefined}
                                  target="_blank"
                                  rel="noreferrer"
                                  size="sm"
                                  variant="subtle"
                                  color="green"
                                >
                                  <IconBrandWhatsapp size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </>
                          ) : null}
                        </Group>
                        <Text size="xs" c="dimmed" truncate maw={220}>
                          {s.owner?.email ?? s.studioEmail ?? '—'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <TierBadge tier={s.effectiveTier} />
                        <StatusBadge status={s.status} />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <ChannelBadge channel={s.channel} sandbox={s.environment === 'SANDBOX'} />
                        {s.expiresAt && s.channel !== 'free' ? (
                          <Text size="xs" c="dimmed">
                            {s.willRenew ? 'Renews' : 'Ends'} {fmtDate(s.expiresAt)}
                          </Text>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">
                          {s.memberCount} member{s.memberCount === 1 ? '' : 's'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {s.clientCount} client{s.clientCount === 1 ? '' : 's'}
                        </Text>
                        {s.seatsPaused > 0 ? (
                          <Badge size="xs" color="orange" variant="light">
                            {s.seatsPaused} paused
                          </Badge>
                        ) : s.seatGraceEndsAt ? (
                          <Badge size="xs" color="yellow" variant="light">
                            Over limit
                          </Badge>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{s.projectCount}</Text>
                        {s.projectsLocked > 0 ? (
                          <Badge size="xs" color="orange" variant="light">
                            {s.projectsLocked} view-only
                          </Badge>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <StorageBar used={s.storageBytes} limit={s.storageLimitBytes} compact />
                        <Text size="xs" c="dimmed">
                          {s.storageFiles.toLocaleString('en-IN')} file{s.storageFiles === 1 ? '' : 's'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <Text size="sm">{fmtDate(s.createdAt)}</Text>
                        <PlatformBadge platform={s.owner?.signupPlatform ?? 'unknown'} />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <RelTime ms={s.lastActiveAt} />
                        {s.owner && s.owner.lastPlatform !== 'unknown' ? (
                          <PlatformBadge
                            platform={s.owner.lastPlatform}
                            suffix={s.owner.appVersion ? `v${s.owner.appVersion}` : undefined}
                          />
                        ) : null}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
      {data ? (
        <Text size="xs" c="dimmed" mt="sm">
          Updated {fmtRelative(data.generatedAt)} ·{' '}
          <Anchor size="xs" onClick={reload}>
            refresh
          </Anchor>
        </Text>
      ) : null}
    </>
  );
}
